import React from 'react';
import classnames from 'classnames/bind';

import { AppEntityObject } from 'hooks/useEntities';

import { Separator } from 'components/Separator/Separator';
import { Text } from 'components/Text/Text';

import { AdvancedSearchBar } from './components/AdvancedSearchBar/AdvancedSearchBar';
import {
  AdvancedSearchProvider,
  AdvancedSearchProviderProps,
} from './AdvancedSearchProvider';
import { AdvancedFilterOptions } from './AdvancedSearch.types';
import styles from './AdvancedSearch.module.scss';

const c = classnames.bind(styles);

export type AdvancedSearchProps<T> = React.PropsWithChildren<
  Omit<AdvancedSearchProviderProps<T>, 'data'> & {
    className?: string;
    searchLabel?: string;
    data?: T[];
    actions?: JSX.Element;
    onSearchInputChange?: (searchInput: string) => void;
    searchOnInputChange?: boolean;
  }
>;

/**
 * Wrapper component that receives & filters data,
 * and populates the context.
 *
 * @deprecated
 */
export function AdvancedSearch<T extends AppEntityObject>({
  entity,
  data = [],
  isLoading,
  filterOptions,
  searchLabel,
  className,
  actions,
  onSearchInputChange,
  searchOnInputChange,
  children,
}: AdvancedSearchProps<T>) {
  const [searchResultCount, setSearchResultCount] = React.useState(data.length);

  // Display entity name in plural or singular depending on amount of results shown
  const type =
    searchResultCount === 1 ? entity.substring(0, entity.length - 1) : entity;

  return (
    <AdvancedSearchProvider
      data={data}
      isLoading={isLoading}
      entity={entity}
      filterOptions={filterOptions}
      onFilteredDataChange={setSearchResultCount}
      onSearchInputChange={onSearchInputChange}
      searchOnInputChange={searchOnInputChange}
    >
      <div className={c('wrap', className)}>
        <div className={c('search-header')}>
          <AdvancedSearchBar label={searchLabel} />
          {Boolean(searchResultCount) && (
            <>
              <Separator orientation="vertical" />
              <Text>
                {searchResultCount} {type}
              </Text>
            </>
          )}
          {actions && <div className={c('actions')}>{actions}</div>}
        </div>
        {children}
      </div>
    </AdvancedSearchProvider>
  );
}

export type { AdvancedFilterOptions };
